import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useIntl } from 'ha/i18n';

import { AppBar, Button, Divider, Grid, Toolbar, units } from '@ha/dsl';
import { LanguageSwitcher } from 'ha/modules/LanguageSwitcher';
import { SmartTrack as Track } from 'ha/components/track/SmartTrack';

import { LoadableSearchBar } from 'ha/modules/SearchBar';
import { usePage } from 'ha/modules/Page/contexts/PageContext';
import { LOGIN, REGISTER } from 'ha/constants/pageNames';

import { useSelector } from 'react-redux';
import {
  getIsAdvertiser,
  getIsAuthenticated,
} from 'ha/modules/AuthLogic/selectors';
import classNames from 'classnames';
import { AdvertiserLogo } from './AdvertiserLogo';
import { NavBarLogo } from './NavBarLogo';
import { UnauthenticatedNavigation } from './UnauthenticatedNavigation';
import { LoadableAuthenticatedNavigation } from '../containers/AuthenticatedNavigation.lazy';
import { PricingLink } from './PricingLink';
import { DropdownMenu } from './DropdownMenu';
import { useListYourPlaceCTA } from '../hooks/useListYourPlaceCTA';

import { getLearnMenuLinks, getProductsMenuLinks } from '../utils';

const useStyles = makeStyles(theme => ({
  appBar: {
    boxShadow: `0px 1px 4px rgba(0, 0, 0, 0.25)`,
  },
  headerContainer: {
    margin: 0,
    display: 'flex',
    flexWrap: 'nowrap',
    gap: theme.spacing(2),
  },
  darkBlue: {
    color: theme.palette.grey[700],
  },
  white: {
    color: theme.palette.white.main,
  },
  searchBar: {
    marginLeft: units.rem(1),
    width: units.rem(20),
  },
  userInfoContainer: {
    maxWidth: 'max-content',
    minWidth: '33%',
    flex: '2 1 100%',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  userInfo: {
    display: 'grid',
    gridAutoFlow: 'column',
  },
  logos: {
    flex: '1 2 33%',
  },
  links: {
    flex: '1 2 33%',
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
  },
  divider: {
    position: 'absolute',
    bottom: 0,
    width: '100%',
  },
  flexItem: {
    display: 'flex',
    alignSelf: 'stretch',
  },
  ukraineHashtag: {
    borderLeft: '1px solid rgba(255, 255, 255, 0.2)',
    paddingLeft: theme.spacing(2),
    marginLeft: theme.spacing(2),
    textTransform: 'uppercase',
    color: '#EED348',
    fontWeight: theme.typography.fontWeightBold,
  },
  ukraineListYourPlace: {
    backgroundColor: theme.palette.white.main,
    color: '#0157B8',
    '&:hover': {
      color: theme.palette.white.main,
    },
  },
  ukraineLanguageSwitcher: {
    color: theme.palette.white.main,
    '&:before': {
      borderBottomColor: theme.palette.white.main,
    },
    '&:hover:before': {
      borderBottomColor: theme.palette.white.main,
    },
  },
}));

interface Props {
  hasStickyNav?: boolean;
  showMiddleMenu?: boolean;
  showSearchBar?: boolean;
  searchBarElement?: React.ReactNode;
  showActions?: boolean;
  showLogo?: boolean;
  advertiserLogo?: string;
  showUkraineFlair?: boolean;
  showDashboardCTA?: boolean;
}

const NavBarDesktop: React.FC<Props> = ({
  hasStickyNav,
  showMiddleMenu = true,
  showSearchBar,
  searchBarElement,
  showActions = true,
  showLogo = true,
  advertiserLogo,
  showUkraineFlair = false,
  showDashboardCTA = false,
}) => {
  const intl = useIntl();
  const { T, urlResolver } = intl;
  const classes = useStyles();
  const ctaProps = useListYourPlaceCTA();
  const { name } = usePage();
  const isAuthPage = name === LOGIN || name === REGISTER;

  const isAuthenticated = useSelector(getIsAuthenticated);
  const isLandlord = useSelector(getIsAdvertiser);

  return (
    <AppBar
      position={hasStickyNav ? 'sticky' : 'relative'}
      className={classes.appBar}
      color="inherit"
      elevation={0}
      data-test-locator="NavBar Top Desktop"
    >
      <Toolbar>
        <Grid
          spacing={1}
          alignItems="center"
          justify="space-between"
          container
          className={classes.headerContainer}
        >
          <Grid item className={classes.logos}>
            <Grid container>
              {advertiserLogo && <AdvertiserLogo url={advertiserLogo} />}
              {showLogo && (
                <NavBarLogo
                  showOnlyIcon={showSearchBar}
                  showUkraineFlair={showUkraineFlair}
                />
              )}
              {showUkraineFlair && (
                <span className={classes.ukraineHashtag}>
                  {T('ukr_response.hashtag_support_ukrainen')}
                </span>
              )}
              {showSearchBar && (
                <div className={classes.searchBar}>
                  {searchBarElement || <LoadableSearchBar />}
                </div>
              )}
            </Grid>
          </Grid>

          {showMiddleMenu && !showUkraineFlair && (
            <Grid
              item
              className={classNames(classes.links, {
                [classes.darkBlue]: !showUkraineFlair,
              })}
              data-test-locator="Navbar Desktop MiddleMenu"
            >
              <DropdownMenu
                id="navbar_desktop_products"
                title={T('navbar_products_button')}
                data={getProductsMenuLinks(intl, !isLandlord)}
                testId="Products NavBar"
              />
              <PricingLink />
              <DropdownMenu
                id="navbar_desktop_learn_menu"
                title={T('navbar_learn_button')}
                data={getLearnMenuLinks(intl, !isLandlord)}
                testId="Learn NavBar"
              />
            </Grid>
          )}

          <Grid item className={classes.userInfoContainer}>
            <Grid
              container
              justify="flex-end"
              alignItems="center"
              spacing={2}
              className={classes.userInfo}
            >
              {!isAuthenticated && !isAuthPage && (
                <Grid item>
                  <UnauthenticatedNavigation
                    showUkraineFlair={showUkraineFlair}
                  />
                </Grid>
              )}

              {ctaProps.isVisible && !showDashboardCTA && (
                <Grid item>
                  <Track name="Clicked List your place in header" type="button">
                    <Button
                      variant={showUkraineFlair ? 'contained' : 'outlined'}
                      color="primary"
                      data-test-locator="List Your Place CTA"
                      href={ctaProps.href}
                      rel={ctaProps.rel}
                      className={
                        showUkraineFlair
                          ? classes.ukraineListYourPlace
                          : undefined
                      }
                    >
                      {ctaProps.label}
                    </Button>
                  </Track>
                </Grid>
              )}

              {showDashboardCTA && (
                <React.Fragment>
                  <Grid item>
                    <Track
                      name="Clicked Dashboard button in header"
                      type="button"
                    >
                      <Button
                        variant="text"
                        color="default"
                        href={urlResolver.getDashboardUrl()}
                      >
                        {T('cta.go_to_dashboard')}
                      </Button>
                    </Track>
                  </Grid>
                  <Grid item className={classes.flexItem}>
                    <Divider
                      dividerProps={{
                        orientation: 'vertical',
                        flexItem: true,
                      }}
                    />
                  </Grid>
                </React.Fragment>
              )}

              {isAuthenticated && (
                <Grid item>
                  <LoadableAuthenticatedNavigation
                    showActions={showActions}
                    showUkraineFlair={showUkraineFlair}
                  />
                </Grid>
              )}

              <Grid item>
                <LanguageSwitcher
                  className={
                    showUkraineFlair
                      ? classes.ukraineLanguageSwitcher
                      : classes.darkBlue
                  }
                  iconClassName={
                    showUkraineFlair ? classes.white : classes.darkBlue
                  }
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Toolbar>
      <Divider className={classes.divider} />
    </AppBar>
  );
};
export { NavBarDesktop };

// eslint-disable-next-line import/no-default-export
export default NavBarDesktop;
